import { createReducer } from 'typesafe-actions';

import { DeletePlannedTaskResponse, TaskState, WorkloadTask } from 'shovel-lib/types';
import * as actions from './actions';
import { ExpandItemType, SidebarItemType, WorkloadState } from './types';
import { sortByNullableDueDateAndCreation } from '../../utils/reducerHelpers';
import { calendarActions } from '../calendar';
import * as taskActions from '../task/actions';
import { setCategoryMinutes } from '../course/actions';
import { now } from 'shovel-lib/utils/timeUtils';
import { GroupByField } from '../../components/course/types';
import { timerActions } from '../timer';
import { tasksUndone } from '../undo/actions';
import { UndoneTask } from '../undo/types';
import { placeMove } from '../../utils/taskListOrder';
import { listFields } from '../../utils/taskPatch';

// "Sort: By hand": the tasks with their new numbers (the shared WorkloadTask type has no listOrder yet).
const withListOrders = (tasks: WorkloadTask[], orders: Map<number, number>) =>
  orders.size === 0
    ? tasks
    : tasks.map(task => (orders.has(task.id) ? ({ ...task, listOrder: orders.get(task.id) } as WorkloadTask) : task));

const initialState: WorkloadState = {
  tasks: [],
  selectedItemType: SidebarItemType.SECTION,
  groupWorkloadBy: GroupByField.DUE_DATE,
  showCompletedWorkload: true,
  newWorkloadTasks: undefined,
  addedTaskIds: undefined,
  loading: false,
  categorizingCourseIds: [],
  pendingEstimateReminders: []
};

const resetNewTasks = (state: WorkloadState, tasks?: WorkloadTask[]) =>
  state.newWorkloadTasks
    ? (Object.values(state.newWorkloadTasks) as WorkloadTask[][]).reduce(
        (tasks, groupTasks: WorkloadTask[]) => tasks.concat(groupTasks),
        tasks || state.tasks
      )
    : tasks || state.tasks;

// Some of a task's fields changed in place, wherever the list keeps it: its tasks, or the ones just added over it.
const withTaskFields = (state: WorkloadState, taskId: number | undefined, fields: Record<string, any>) => {
  if (!taskId || !Object.keys(fields).length) return state;
  const apply = (task: WorkloadTask) => (task.taskId === taskId ? { ...task, ...fields } : task);
  const newWorkloadTasks = state.newWorkloadTasks
    ? Object.fromEntries(Object.entries(state.newWorkloadTasks).map(([key, tasks]) => [key, tasks.map(apply)]))
    : state.newWorkloadTasks;
  return { ...state, tasks: state.tasks.map(apply), newWorkloadTasks };
};

// A task taken back out of Completed (Undo, Revert, Mark incomplete) is overdue again when its due date has passed - the
// server answers "active" and works out overdue only when the list is loaded. As plain active, the Week grouping put an
// undone overdue task into its due week, without its 🔥, until a reload.
const reopenedState = (task: WorkloadTask, state: TaskState) =>
  state === TaskState.ACTIVE && !!task.dueDate && now().isAfter(task.dueDate) ? TaskState.DO_LATER : state;

// A task the server sends back after an edit has no Planned minutes (only the Task List's own load works them out): keep
// the ones the list has.
const keepPlanned = (task: WorkloadTask, updated?: WorkloadTask): WorkloadTask =>
  updated ? ({ ...updated, timePlanned: (updated as any).timePlanned ?? (task as any).timePlanned } as WorkloadTask) : task;

const reducer = createReducer(initialState)
  .handleAction(actions.getWorkload.success, (state, action) => ({
    ...state,
    tasks: action.payload,
    newWorkloadTasks: undefined,
    addedTaskIds: undefined
  }))
  .handleAction(actions.setCourseCategorizing, (state, { payload }) => ({
    ...state,
    categorizingCourseIds: payload.categorizing
      ? [...state.categorizingCourseIds.filter(id => id !== payload.courseId), payload.courseId]
      : state.categorizingCourseIds.filter(id => id !== payload.courseId)
  }))
  .handleAction(actions.setPendingEstimateReminder, (state, { payload }) => ({
    ...state,
    pendingEstimateReminders: payload.pending
      ? [...state.pendingEstimateReminders.filter(id => id !== payload.courseId), payload.courseId]
      : state.pendingEstimateReminders.filter(id => id !== payload.courseId)
  }))
  .handleAction([calendarActions.updateCourse.success, calendarActions.updateActivity.success], (state, action) => {
    return {
      ...((state.selectedItemType === SidebarItemType.COURSE || state.selectedItemType === SidebarItemType.ACTIVITY) &&
      state.selectedItemId === action.payload.id
        ? {
            ...state,
            selectedItemName: action.payload.name,
            selectedItemColor: action.payload.colorHex
          }
        : state),
      tasks: resetNewTasks(state).map(t =>
        t.courseId === action.payload.id
          ? {
              ...t,
              courseName: action.payload.name,
              colorHex: action.payload.colorHex
            }
          : t
      ),
      newWorkloadTasks: undefined,
      addedTaskIds: undefined
    };
  })
  .handleAction(taskActions.createTasks.success, (state, action) => {
    const { workloadTable, listOrders, copyOf } = action.payload;

    const added = workloadTable
      ? state.tasks
      : resetNewTasks(state, [...state.tasks, ...action.payload.tasks]).sort(sortByNullableDueDateAndCreation);
    // A copy sorted by hand: its place under its original (and any numbers given with it), at once.
    const tasks = listOrders
      ? added.map(task => (listOrders[task.id] !== undefined ? { ...task, listOrder: listOrders[task.id] } : task))
      : added;

    // Added over the list or next to a course's name: sorted into the group it belongs to, but kept at that group's
    // end, in the order added, until the view changes - as a group's own "+ Add new task" keeps them (Petr).
    // A copy made by Duplicate isn't held there: it shows right under its original (Petr).
    const addedTaskIds =
      workloadTable || copyOf ? state.addedTaskIds : [...(state.addedTaskIds || []), ...action.payload.tasks.map(t => t.id)];

    let newWorkloadTasks: any = undefined;
    if (workloadTable) {
      newWorkloadTasks = state.newWorkloadTasks
        ? {
            ...state.newWorkloadTasks,
            [workloadTable]: [...(state.newWorkloadTasks[workloadTable] || []), ...action.payload.tasks]
          }
        : { [workloadTable]: action.payload.tasks };
    }

    return {
      ...state,
      tasks,
      newWorkloadTasks,
      addedTaskIds
    };
  })
  // Moved at once, where the server will put it; its answer then settles the numbers (the same, or renumbered).
  .handleAction(actions.moveTaskInList.request, (state, { payload }) => ({
    ...state,
    tasks: withListOrders(state.tasks, placeMove(state.tasks as any[], payload.move))
  }))
  .handleAction(actions.moveTaskInList.success, (state, { payload }) => ({
    ...state,
    tasks: withListOrders(state.tasks, new Map(payload.map(({ id, listOrder }) => [id, listOrder])))
  }))
  .handleAction(actions.resetAddedWorkloadTasks, state => {
    return {
      ...state,
      tasks: resetNewTasks(state),
      newWorkloadTasks: undefined,
      addedTaskIds: undefined
    };
  })
  .handleAction(calendarActions.deleteCourse.success, (state, action) => {
    if (state.selectedItemType !== SidebarItemType.COURSE || state.selectedItemId !== action.payload.id) return state;
    return initialState;
  })
  .handleAction(calendarActions.deleteActivity.success, (state, action) => {
    if (state.selectedItemType !== SidebarItemType.ACTIVITY || state.selectedItemId !== action.payload.id) return state;
    return initialState;
  })
  .handleAction(actions.selectCommitmentSuccess, state => ({ ...state, loading: false }))
  .handleAction(actions.selectCommitment, (state, action) => ({
    ...state,
    loading: true,
    selectedItemId: action.payload?.id,
    selectedItemType: action.payload?.type || SidebarItemType.SECTION,
    selectedItemName: action.payload?.name,
    selectedItemColor: action.payload?.colorHex,
    selectedItemCourseId: action.payload?.courseId || undefined
  }))
  // Moving to the trash leaves the list at once, so its animation starts on the click rather than after the server
  // answers (Petr: "a big lag"); a refused move brings the tasks back (restoreTasks.success from the saga).
  .handleAction(taskActions.deleteTask.request, (state, { payload }) =>
    payload.deleteForever
      ? state
      : { ...state, tasks: state.tasks.map(t => (t.taskId === payload.id ? { ...t, deleted: true } : t)) }
  )
  .handleAction(taskActions.deleteTasks.request, (state, { payload }) =>
    payload.deleteForever
      ? state
      : { ...state, tasks: state.tasks.map(t => (payload.ids.includes(t.id) ? { ...t, deleted: true } : t)) }
  )
  .handleAction(taskActions.deleteTasks.success, (state, action) => {
    const tasks = resetNewTasks(state);
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks: action.payload.deleteForever
        ? tasks.filter(t => !action.payload.ids.includes(t.id))
        : tasks.map(t => (action.payload.ids.includes(t.id) ? { ...t, deleted: true } : t))
    };
  })
  .handleAction(taskActions.restoreTasks.success, (state, action) => {
    return {
      ...state,
      tasks: state.tasks.map(t => (action.payload.includes(t.id) ? { ...t, deleted: false } : t))
    };
  })
  .handleAction(tasksUndone, (state, action) => {
    const undone = new Map<number, UndoneTask>(action.payload.tasks.map((task: UndoneTask) => [task.taskId, task]));
    return {
      ...state,
      tasks: state.tasks.map(t => {
        const task = undone.get(t.id);
        return task
          ? { ...t, state: reopenedState(t, task.state), completedOn: task.completedOn || undefined, deleted: task.deleted }
          : t;
      })
    };
  })
  .handleAction(taskActions.completeTasks.success, (state, action) => {
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks: resetNewTasks(state).map(t =>
        action.payload.includes(t.id) ? ({ ...t, state: TaskState.COMPLETED, timePlanned: 0 } as WorkloadTask) : t
      )
    };
  })
  .handleAction(taskActions.moveTasksToTodo.success, (state, action) => {
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks: resetNewTasks(state).map(t =>
        action.payload.includes(t.id) ? { ...t, state: reopenedState(t, TaskState.ACTIVE) } : t
      )
    };
  })
  .handleAction(taskActions.setTasksStartAhead.success, (state, { payload }) => {
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks: resetNewTasks(state).map(t =>
        payload.taskIds.includes(t.id) ? { ...t, daysToStartAhead: payload.daysToStartAhead } : t
      )
    };
  })
  .handleAction(taskActions.setTasksPriority.success, (state, { payload }) => {
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks: resetNewTasks(state).map(t => (payload.taskIds.includes(t.id) ? { ...t, priority: payload.priority } : t))
    };
  })
  .handleAction(
    [
      taskActions.setTasksDueDate.success,
      taskActions.setTasksDueTime.success,
      taskActions.pushTasksDueDate.success,
      taskActions.tasksEstimationByCategory.success
    ],
    (state, { payload }) => {
      return {
        ...state,
        newWorkloadTasks: undefined,
        addedTaskIds: undefined,
        tasks: resetNewTasks(state)
          .map(task =>
            keepPlanned(
              task,
              payload.find(t => t.id === task.id)
            )
          )
          .sort(sortByNullableDueDateAndCreation)
      };
    }
  )
  .handleAction(taskActions.estimateTasks.success, (state, { payload }) => {
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks: resetNewTasks(state)
        .map(task =>
          keepPlanned(
            task,
            payload.tasks.find(t => t.id === task.id)
          )
        )
        .sort(sortByNullableDueDateAndCreation)
    };
  })
  // A category's time given to its tasks still to do: each takes its new time in place.
  .handleAction(setCategoryMinutes.success, (state, { payload }) => ({
    ...state,
    tasks: state.tasks.map(task =>
      keepPlanned(
        task,
        payload.tasks.find(t => t.id === task.id)
      )
    )
  }))
  .handleAction(taskActions.tasksCategorization.success, (state, { payload }) => {
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks: resetNewTasks(state)
        .map(task =>
          keepPlanned(
            task,
            payload.tasks.find(t => t.id === task.id)
          )
        )
        .sort(sortByNullableDueDateAndCreation)
    };
  })
  .handleAction(taskActions.setTimeNeeded.success, (state, { payload }) => {
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks: resetNewTasks(state).map(task =>
        payload.taskIds.includes(task.id)
          ? {
              ...task,
              timeNeed: payload.timeNeeded,
              daysToStartAhead: payload.startAhead?.[task.id] ?? task.daysToStartAhead
            }
          : task
      )
    };
  })
  .handleAction(taskActions.addPageRangeForTasks.success, (state, { payload }) => {
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks: resetNewTasks(state).map(task =>
        keepPlanned(
          task,
          payload.find(t => t.id === task.id)
        )
      )
    };
  })
  .handleAction(taskActions.savePageRanges.success, (state, { payload }) => {
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks: resetNewTasks(state).map(task => (payload.data.taskId === task.id ? keepPlanned(task, payload.data) : task))
    };
  })
  .handleAction([timerActions.start.success, calendarActions.markAsUsed.success], (state, { payload }) => {
    if (!payload.plannedTaskDto) return state;
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks: resetNewTasks(state).map(task =>
        payload.taskId === task.id
          ? { ...task, timeNeed: payload.timeNeed, timeSpent: payload.timeSpent, pages: payload.pages }
          : task
      )
    };
  })
  .handleAction(calendarActions.updatePlannedTask.success, (state, { payload }) => {
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks: resetNewTasks(state).map(task =>
        payload.data.taskId === task.id
          ? { ...task, timeNeed: payload.data.timeNeed, timeSpent: payload.data.timeSpent }
          : task
      )
    };
  })
  .handleAction(calendarActions.deletePlannedTask.success, (state, { payload }) => {
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks: resetNewTasks(state).map(task =>
        payload.event.taskId === task.id
          ? { ...task, timeNeed: payload.data.timeNeed, timeSpent: payload.data.timeSpent }
          : task
      )
    };
  })
  .handleAction(calendarActions.markBlocksUsed.success, (state, { payload }) => {
    if (payload.tasks.length === 0) return state;
    const dataByTaskId = new Map<number, DeletePlannedTaskResponse>(payload.tasks.map(data => [data.taskId, data]));
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks: resetNewTasks(state).map(task => {
        const data = dataByTaskId.get(task.id);
        return data ? { ...task, timeNeed: data.timeNeed, timeSpent: data.timeSpent } : task;
      })
    };
  })
  .handleAction(calendarActions.unplanWeek.success, (state, { payload }) => {
    if (payload.tasks.length === 0) return state;
    const dataByTaskId = new Map<number, DeletePlannedTaskResponse>(payload.tasks.map(data => [data.taskId, data]));
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks: resetNewTasks(state).map(task => {
        const data = dataByTaskId.get(task.id);
        return data ? { ...task, timeNeed: data.timeNeed, timeSpent: data.timeSpent } : task;
      })
    };
  })
  .handleAction(taskActions.moveToTodo.success, (state, { payload }) => {
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks: resetNewTasks(state).map(task =>
        payload.taskId === task.id
          ? {
              ...task,
              state: reopenedState(task, TaskState.ACTIVE),
              completedOn: undefined
            }
          : task
      )
    };
  })
  .handleAction(taskActions.updateTaskStatus.success, (state, { payload }) => {
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks: resetNewTasks(state).map(task =>
        payload.taskId === task.id
          ? {
              ...task,
              state: payload.state,
              completedOn: payload.completedOn,
              // Completing removes the sessions still ahead.
              ...(payload.state === TaskState.COMPLETED ? { timePlanned: 0 } : {})
            }
          : task
      )
    };
  })
  .handleAction(taskActions.deleteTask.success, (state, { payload }) => {
    const tasks = payload.deleteForever
      ? resetNewTasks(state).filter(t => t.id !== payload.id)
      : resetNewTasks(state).map(t => (t.taskId === payload.id ? { ...t, deleted: true } : t));
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks
    };
  })
  // A tick, rename or delete of a subtask shows at once (the Task List's subtask lines and their counts), before the
  // server answers. A new one too when added on the lines (tempId), last in its task's order, till the server's copy
  // takes its place: the text typed doesn't vanish and come back (Petr). One from elsewhere waits for its id.
  .handleAction(taskActions.addOrUpdateSubtask.request, (state, { payload }) => {
    if (payload.data.id === undefined) {
      if (payload.tempId === undefined) return state;
      return {
        ...state,
        tasks: state.tasks.map(t => {
          if (t.id !== payload.taskId) return t;
          const last = Math.max(0, ...(t.subtasks || []).map(s => s.ordinalNumber ?? 0));
          return {
            ...t,
            subtasks: [...(t.subtasks || []), { ...payload.data, id: payload.tempId, ordinalNumber: last + 1 }]
          };
        })
      };
    }
    return {
      ...state,
      tasks: state.tasks.map(t =>
        t.id !== payload.taskId
          ? t
          : { ...t, subtasks: t.subtasks.map(s => (s.id === payload.data.id ? { ...s, ...payload.data } : s)) }
      )
    };
  })
  // Not saved: a new subtask shown ahead of the server's answer goes again.
  .handleAction(taskActions.addOrUpdateSubtask.failure, state => ({
    ...state,
    tasks: state.tasks.map(t =>
      t.subtasks?.some(s => (s.id ?? 0) < 0) ? { ...t, subtasks: t.subtasks.filter(s => (s.id ?? 0) >= 0) } : t
    )
  }))
  .handleAction(taskActions.deleteSubtask.request, (state, { payload }) => ({
    ...state,
    tasks: state.tasks.map(t =>
      t.id !== payload.taskId ? t : { ...t, subtasks: t.subtasks.filter(s => s.id !== payload.id) }
    )
  }))
  .handleAction(taskActions.addOrUpdateSubtask.success, (state, { payload }) => {
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks: resetNewTasks(state).map(t => {
        if (t.id !== payload.taskId) return t;
        const subtasks = [...t.subtasks];
        const subtaskIndex = subtasks.findIndex(
          s => s.id === payload.data.id || (payload.tempId !== undefined && s.id === payload.tempId)
        );
        if (subtaskIndex !== -1) {
          subtasks[subtaskIndex] = payload.data;
        } else {
          subtasks.push(payload.data);
        }
        return { ...t, subtasks };
      })
    };
  })
  .handleAction(taskActions.deleteSubtask.success, (state, { payload }) => {
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks: resetNewTasks(state).map(t => {
        if (t.id !== payload.taskId) return t;
        return { ...t, subtasks: t.subtasks.filter(s => s.id !== payload.id) };
      })
    };
  })
  // An edit shows at once - the name typed, the due date picked: the save reads the task first, so its answer comes later
  // and the old value showed in between (Petr). The answer brings the rest (a Start ahead that follows the time).
  .handleAction(taskActions.saveManageTask.request, (state, { payload }) =>
    withTaskFields(state, payload.task?.taskId, listFields(payload.request))
  )
  .handleAction(taskActions.restoreTaskFields, (state, { payload }) => withTaskFields(state, payload.taskId, payload.fields))
  .handleAction(taskActions.saveManageTask.success, (state, { payload }) => {
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks: resetNewTasks(state).map(t => {
        if (t.taskId !== payload.response.taskId) return t;
        return { ...t, ...payload.response };
      })
    };
  })
  .handleAction(taskActions.changeTaskCategory.success, (state, action) => {
    const { taskIds, category } = action.payload;
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks: resetNewTasks(state).map(task => {
        if (!taskIds.includes(task.id)) return task;
        return { ...task, taskCategory: category };
      })
    };
  })
  .handleAction(taskActions.changeReadingSource.success, (state, action) => {
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks: resetNewTasks(state).map(t => {
        const updatedTask = action.payload.find(task => task.taskId === t.id);
        if (!updatedTask) return t;
        return {
          ...t,
          ...updatedTask,
          pageRanges: updatedTask.pageRanges || t.pageRanges
        };
      })
    };
  })
  .handleAction(taskActions.changeCourse.success, (state, action) => {
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks: resetNewTasks(state).map(t => {
        const updatedTask = action.payload.find(task => task.taskId === t.id);
        if (!updatedTask) return t;
        return {
          ...t,
          ...updatedTask
        };
      })
    };
  })
  // Reordered by dragging in the Task List: shown at once, as ticks and renames are.
  .handleAction(taskActions.reorderSubtasks.request, (state, { payload }) => ({
    ...state,
    tasks: state.tasks.map(t => (t.taskId !== payload.id ? t : { ...t, subtasks: payload.subtasks }))
  }))
  .handleAction(taskActions.reorderSubtasks.success, (state, action) => {
    const { id, subtasks } = action.payload;
    return {
      ...state,
      newWorkloadTasks: undefined,
      addedTaskIds: undefined,
      tasks: resetNewTasks(state).map(t => {
        if (t.taskId !== id) return t;
        return {
          ...t,
          subtasks
        };
      })
    };
  })
  .handleAction(actions.toggleCourseSection, (state, action) => ({
    ...state,
    expandedItemType: action.payload.expand ? ExpandItemType.COURSE : undefined,
    expandedItemId: action.payload.expand ? action.payload.id : undefined
  }))
  .handleAction(calendarActions.addTaskCategory, (state, action) => {
    if (state.expandedItemId === action.payload) {
      return state;
    }
    return {
      ...state,
      expandedItemType: ExpandItemType.COURSE,
      expandedItemId: action.payload
    };
  });

export default reducer;
